import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Minus, Plus, ShoppingCart, Star, Truck, RotateCcw, Eye, Flame, Building2, FileText } from "lucide-react";
import { toast } from "sonner";
import { productById, catBySlug, PRODUCTS, fmt, bezPvn, demoStats } from "../data/store";
import { useCart } from "../context/CartContext";
import { ProductCard } from "../components/ProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function ProductPage() {
  const { id } = useParams();
  const product = productById(id);
  const { add } = useCart();
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!product) return;
    try {
      const r = (JSON.parse(localStorage.getItem("demo_recent")) || []).filter((x) => x !== product.id);
      localStorage.setItem("demo_recent", JSON.stringify([product.id, ...r].slice(0, 10)));
    } catch { /* noop */ }
  }, [product]);

  if (!product) return <div className="max-w-7xl mx-auto px-4 mt-16 text-center"><p className="text-lg font-bold">Prece nav atrasta.</p><Link to="/" className="text-[#111111] underline text-sm">Atpakaļ uz sākumu</Link></div>;

  const cat = catBySlug(product.category);
  const stats = demoStats(product);
  const similar = PRODUCTS.filter((x) => x.category === product.category && x.id !== product.id).slice(0, 4);
  const together = PRODUCTS.filter((x) => x.category !== product.category).sort((a, b) => b.rating - a.rating).slice(0, 4);
  const complementary = PRODUCTS.filter((x) => x.brand === product.brand && x.id !== product.id).slice(0, 4);
  const alsoViewed = PRODUCTS.filter((x) => x.id !== product.id && demoStats(x).soldWeek > 40).slice(0, 4);
  const packPrice = product.price * product.packQty;

  const handleAdd = () => {
    add(product, qty);
    toast.success("Pievienots grozam", { description: `${product.name} × ${qty}` });
  };

  const specs = [
    ["Artikuls (SKU)", product.sku],
    ["Zīmols", product.brand],
    ...(product.volume ? [["Tilpums / svars", product.volume]] : []),
    ...(product.material ? [["Materiāls", product.material]] : []),
    ...(product.color ? [["Krāsa", product.color]] : []),
    ...(product.size ? [["Izmērs", product.size]] : []),
    ["Iepakojumā", `${product.packQty} gab.`],
    ["EAN", product.ean],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 mt-6">
      <nav className="flex items-center gap-1.5 text-xs text-[#6B6B6B] mb-6 flex-wrap" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#111111] hover:underline">Sākums</Link>
        <ChevronRight size={12} />
        <Link to={`/kategorija/${cat.slug}`} className="hover:text-[#111111] hover:underline">{cat.name}</Link>
        <ChevronRight size={12} />
        <Link to={`/kategorija/${cat.slug}?sub=${encodeURIComponent(product.subcategory)}`} className="hover:text-[#111111] hover:underline">{product.subcategory}</Link>
        <ChevronRight size={12} />
        <span className="text-[#111111] font-semibold line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className={`aspect-square bg-[#F5F5F5] rounded-md overflow-hidden border border-[#E2E2E2] cursor-zoom-in ${zoom ? "cursor-zoom-out" : ""}`} onClick={() => setZoom(!zoom)} data-testid="pdp-main-image">
            <img src={product.image} alt={product.name} className={`w-full h-full object-cover mix-blend-multiply transition-transform duration-300 ${zoom ? "scale-150" : ""}`} />
          </div>
          <div className="flex gap-2 mt-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`w-16 h-16 rounded-md overflow-hidden border cursor-pointer ${i === 0 ? "border-[#111111]" : "border-[#E2E2E2]"}`}>
                <img src={product.image} alt="" className="w-full h-full object-cover mix-blend-multiply bg-[#F5F5F5]" style={{ transform: `scale(${1 + i * 0.3})` }} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex gap-2 mb-2">
            {product.oldPrice && <span className="bg-[#D0121A] text-white text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">Akcija</span>}
            {product.isNew && <span className="bg-[#111111] text-white text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">Jaunums</span>}
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight" data-testid="pdp-title">{product.name}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-[#6B6B6B] flex-wrap">
            <span>SKU: <span className="font-semibold text-[#111111]">{product.sku}</span></span>
            <span>·</span>
            <span>Zīmols: <span className="font-semibold text-[#111111]">{product.brand}</span></span>
            <span>·</span>
            <span className="flex items-center gap-1"><Star size={14} className="fill-[#E5A000] text-[#E5A000]" /> {product.rating.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-3">
            <span className={`w-2.5 h-2.5 rounded-full ${product.stock ? "bg-[#1E9E45]" : "bg-[#E5A000]"}`} />
            <span className="text-sm font-semibold" data-testid="pdp-availability">{product.stock ? "Noliktavā" : "Pēc pasūtījuma (3–5 darba dienas)"}</span>
            {stats.b2bPopular && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#111111] text-white px-2 py-0.5" data-testid="pdp-b2b-popular">Populārs uzņēmumu klientiem</span>}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-[#6B6B6B] flex-wrap" data-testid="pdp-social-proof">
            <span className="flex items-center gap-1"><Eye size={13} /> {stats.viewers} cilvēki šobrīd skatās</span>
            <span className="flex items-center gap-1"><Flame size={13} className="text-[#D0121A]" /> Pārdoti {stats.soldWeek} gab. šonedēļ</span>
          </div>
          {stats.low && (
            <div className="mt-3 max-w-xs" data-testid="pdp-low-stock">
              <div className="text-xs font-bold text-[#D0121A] mb-1">Palicis maz noliktavā: {stats.stockLevel} gab.</div>
              <div className="h-1.5 bg-[#E2E2E2]"><div className="h-full bg-[#D0121A]" style={{ width: `${Math.min(100, stats.stockLevel * 8)}%` }} /></div>
            </div>
          )}

          <div className="mt-6 bg-[#F5F5F5] rounded-md p-5">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-extrabold" data-testid="pdp-price">{fmt(product.price)}</span>
              {product.oldPrice && <span className="text-lg line-through text-[#6B6B6B]">{fmt(product.oldPrice)}</span>}
              <span className="text-sm text-[#6B6B6B]">ar PVN / gab.</span>
            </div>
            <div className="text-sm text-[#6B6B6B] mt-1">{fmt(bezPvn(product.price))} bez PVN</div>
            {product.packQty > 1 && (
              <div className="mt-3 pt-3 border-t border-[#E2E2E2] text-sm" data-testid="pdp-pack-info">
                <span className="font-semibold">Iepakojumā: {product.packQty} gab.</span>
                <span className="text-[#6B6B6B]"> · Cena par iepakojumu: <span className="font-bold text-[#111111]">{fmt(packPrice)}</span> ar PVN</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center border border-[#E2E2E2] rounded-md">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#F5F5F5]" data-testid="pdp-qty-minus" aria-label="Mazāk"><Minus size={16} /></button>
              <span className="w-12 text-center font-bold" data-testid="pdp-qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#F5F5F5]" data-testid="pdp-qty-plus" aria-label="Vairāk"><Plus size={16} /></button>
            </div>
            <button onClick={handleAdd} data-testid="pdp-add-to-cart"
              className="flex-1 bg-[#D0121A] hover:bg-[#A80D14] text-white font-bold py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors">
              <ShoppingCart size={18} /> Pievienot grozam
            </button>
          </div>
          {!product.stock && (
            <button onClick={() => toast.success("Pieprasījums nosūtīts (DEMO)", { description: "Cena pēc pieprasījuma — menedžeris sagatavos piedāvājumu 1 darba dienā." })}
              data-testid="pdp-price-request" className="w-full mt-3 border-2 border-[#111111] hover:bg-[#111111] hover:text-white font-bold py-3 rounded-md transition-colors flex items-center justify-center gap-2">
              <FileText size={16} /> Cena pēc pieprasījuma — pieprasīt piedāvājumu
            </button>
          )}
          <button onClick={() => toast.info("B2B līguma cenas (DEMO)", { description: "Pieslēdzieties B2B kontam, lai redzētu jūsu uzņēmuma līguma cenu šai precei." })}
            data-testid="pdp-contract-price" className="w-full mt-3 bg-[#F5F5F5] hover:bg-[#E2E2E2] text-left px-4 py-3 flex items-center gap-3 transition-colors">
            <Building2 size={18} className="text-[#D0121A]" />
            <span className="text-xs"><span className="font-bold">Līguma cenas šim klientam:</span> pieslēdzies B2B kontam, lai redzētu savu cenu</span>
          </button>

          <div className="flex gap-5 mt-5 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1.5"><Truck size={14} /> Piegāde 1–3 darba dienās</span>
            <span className="flex items-center gap-1.5"><RotateCcw size={14} /> 14 dienu atgriešana</span>
          </div>

          <Tabs defaultValue="apraksts" className="mt-8">
            <TabsList className="w-full justify-start rounded-md bg-[#F5F5F5]">
              <TabsTrigger value="apraksts" data-testid="tab-apraksts">Apraksts</TabsTrigger>
              <TabsTrigger value="specifikacijas" data-testid="tab-specifikacijas">Specifikācijas</TabsTrigger>
              <TabsTrigger value="piegade" data-testid="tab-piegade">Piegāde un atgriešana</TabsTrigger>
            </TabsList>
            <TabsContent value="apraksts" className="text-sm leading-relaxed text-[#111111] pt-2">{product.description}</TabsContent>
            <TabsContent value="specifikacijas" className="pt-2">
              <table className="w-full text-sm">
                <tbody>
                  {specs.map(([k, v]) => (
                    <tr key={k} className="border-b border-[#E2E2E2]">
                      <td className="py-2 text-[#6B6B6B] w-1/2">{k}</td>
                      <td className="py-2 font-semibold">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TabsContent>
            <TabsContent value="piegade" className="text-sm leading-relaxed text-[#111111] pt-2 space-y-2">
              <p><strong>Kurjers:</strong> 1–3 darba dienas, 4,99 € (bezmaksas virs 60 €).</p>
              <p><strong>Pakomāts:</strong> 1–3 darba dienas, 2,99 €.</p>
              <p><strong>Saņemšana demo veikalā:</strong> bez maksas, tajā pašā dienā.</p>
              <p><strong>Atgriešana:</strong> 14 dienu laikā, ja prece nav lietota un ir oriģinālajā iepakojumā.</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight uppercase mb-5">Alternatīvas šai precei</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {similar.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight uppercase mb-5">Bieži pērk kopā</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {together.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
      {complementary.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold tracking-tight uppercase mb-5">Papildinošās preces ({product.brand})</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {complementary.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight uppercase mb-5">Klienti skatīja arī</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {alsoViewed.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-[#E2E2E2] p-3 z-40 flex items-center gap-3">
        <div>
          <div className="font-bold">{fmt(product.price)}</div>
          <div className="text-[10px] text-[#6B6B6B]">ar PVN</div>
        </div>
        <button onClick={handleAdd} data-testid="pdp-sticky-add-to-cart"
          className="flex-1 bg-[#D0121A] text-white font-bold py-3 rounded-md flex items-center justify-center gap-2">
          <ShoppingCart size={16} /> Pievienot grozam
        </button>
      </div>
    </div>
  );
}
